<template>
    <Page class="help">
        <div class="welcome-header">
            <h1>Help & Documentation</h1>
            <p class="subtitle">
                Learn about MyStekz and how to build a superintelligent business
                — together with your team and the AI agents working alongside
                them.
            </p>
        </div>

        <Card class="content-card introduction">
            <template #content>
                <p>
                    MyStekz is for building the superintelligent business: one
                    place where people and AI agents collaborate on every layer
                    — strategy, design, code, operations — against a shared
                    model of how the business actually works. To grasp the full
                    power of MyStekz there are a few fundamental aspects to
                    understand.
                </p>
            </template>
        </Card>

        <Card class="content-card">
            <template #title>
                <h2>What is a Stek?</h2>
            </template>
            <template #content>
                <p>
                    Imagine your business as a well-oiled machine, where every
                    part needs to work together seamlessly to achieve success. A
                    Stek is like the blueprint for this machine. It starts with
                    the business strategies and processes that guide what you do
                    and why you do it. Then it also includes the technical
                    components like the software and hardware required achieve
                    your goals.
                </p>
                <p>
                    In simpler terms, a Stek is a comprehensive plan that aligns
                    your technology tools with your business goals. It ensures
                    that everything from your customer-facing applications to
                    your internal systems, and even your business strategies,
                    are all working in harmony. This way, you can operate more
                    efficiently and effectively, making sure that every part of
                    your business supports your overall objectives.
                </p>
                <p>
                    In MyStekz, your Stek comes to live by creating an organized
                    collection of all the digital items that make up your
                    digital business. Design documents, source code,
                    discussions, domain models, functional requirements,
                    ...everything. To keep track of things MyStekz organizes
                    items into related categories. Guiding principles are
                    <a
                        href="https://stekz.com/mystekz-introduction/"
                        target="_blank"
                    >
                        Domain Driven Design
                    </a>
                    and
                    <a
                        href="https://stekz.com/the-bizdevops-advantage/"
                        target="_blank"
                    >
                        BizDevOps
                    </a>
                    . The key takeaway for now is the following way of thinking:
                </p>
                <ul>
                    <li>A business operates in one or more <b>domains</b></li>
                    <li>
                        A domain offers <b>products</b> and/or <b>services</b>
                    </li>
                    <li>A product is the result of <b>processes</b></li>
                </ul>
            </template>
        </Card>

        <Card class="content-card">
            <template #title>
                <h2>Why organize it like this?</h2>
            </template>
            <template #content>
                <p>
                    Of course, any real business is a very complex system with
                    many moving parts. To stay in control of large information
                    systems, organizations are cut up into Domains, Products,
                    and Processes when using MyStekz. It is based on well-known,
                    working solutions like
                    <a
                        href="https://martinfowler.com/bliki/DomainDrivenDesign.html"
                        target="_blank"
                        >Domain-Driven Design (DDD)</a
                    >. A domain can be defined as a main area of knowledge and
                    activity in a business, shaped by its rules and logic. Each
                    domain offers products and services to the outside world.
                    These products and services are realized by processes.
                </p>
            </template>
        </Card>

        <Card class="content-card">
            <template #title>
                <h2>Practical example</h2>
            </template>
            <template #content>
                <p>
                    In this example, your pizzeria is modeled across multiple
                    levels of detail. Let’s start with defining some domains,
                    the main areas of knowledge, and activity in your pizzeria.
                    We can define at least Sales, Production, and Delivery as
                    domains of a simple pizzeria. To optimize the delivery of
                    online orders, we have to dive into the Delivery domain,
                    specifically the Planning product we can define underneath
                    it. Let’s call the relevant process Plan online order
                    delivery.
                </p>
                <p class="image-container">
                    <img
                        id="example_domain"
                        alt="example_domain"
                        src="../assets/docs/Domains-Products-and-Processes-Pizzeria.png"
                    />
                </p>
            </template>
        </Card>

        <Card class="content-card">
            <template #title>
                <h2>Under the hood</h2>
            </template>
            <template #content>
                <p>
                    Guiding principle of Stekz is the empowerment of operators,
                    developers and business related roles. But also that users
                    of MyStekz are in total control of their own data. That is
                    why MyStekz hosts all content as Git repositories on its own
                    or external Git servers. To keep things modular so teams can
                    work independently on specific areas of a Stek, each Stek
                    consists of a hierarchy of Git repositories:
                </p>
                <p class="image-container">
                    <img
                        id="git_tree"
                        alt="git_tree"
                        src="../assets/docs/overview_git_tree.png"
                    />
                </p>
                <p>
                    This means all content is version controlled and can be
                    worked on in a distributed manner. For instance, if a
                    development team wants to locally develop a specific
                    application ('dev') they simply checkout that repository.
                    The important thing to realize is that there is always a
                    clear relationship between items. Mentioned development team
                    can always easily find and access things like the related
                    BPMN process, product documentation, functional
                    requirements, domain model, etcetera.
                </p>
            </template>
        </Card>
    </Page>
</template>

<script setup lang="ts">
// Import the Card component from PrimeVue (same as used in OrganizationHome)
import Card from "primevue/card";

import Page from "@/components/Page.vue";
</script>

<style lang="scss" scoped>
.help {
    --p-card-background: var(--surface-background-mild);
    --p-card-subtitle-color: var(--text-color);
    --p-card-color: var(--text-color);

    max-width: 1200px;

    .welcome-header {
        text-align: center;
        margin-bottom: var(--default-gap);

        h1 {
            font-size: var(--text-xxl);
            margin-bottom: 0.5rem;
        }

        .subtitle {
            /* color: var(--text-color); */
            margin: 0;
        }
    }

    .content-card {
        margin-bottom: var(--default-gap);

        p {
            color: var(--p-card-color);
        }

        &.introduction {
            --p-card-background: var(--stekz-primary-color);
            --p-card-color: var(--stekz-white);
            /* border-left: 5px solid var(--stekz-secondary-color); */
        }
    }

    :deep(.p-card-title) {
        h2 {
            font-size: inherit;
            /* color: var(--text-color); */
        }
    }

    :deep(.p-card-content) {
        p {
            /* color: var(--text-color); */
            margin-bottom: 1rem;
        }

        ol,
        ul {
            padding-left: 2rem;

            li {
                margin-bottom: 0.5rem;
            }
        }
    }
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
    .help {
        padding: 0.5rem;

        .welcome-header {
            h1 {
                font-size: 1.5rem;
            }
        }

        :deep(.p-card-title) {
            h2 {
                font-size: 1.3rem;
            }
        }
    }
}
</style>
